import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const customId = "custom-id-yes";

export const listUsers = createAsyncThunk(
  "users/listUsers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await Axios.get(`${BASE_URL}/api/users`);
      return data;
    } catch (error) {
      const err =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return rejectWithValue(err);
    }
  }
);

export const signin = createAsyncThunk(
  "users/signin",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await Axios.post(`${BASE_URL}/auth/login`, credentials);
      toast.success("Welcome!", {
        toastId: customId,
      });
      localStorage.setItem("userInfo", JSON.stringify(data));
      return data;
    } catch (error) {
      const err =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      toast.error(err, {
        toastId: customId,
      });
      return rejectWithValue(err);
    }
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (userId, { rejectWithValue, getState }) => {
    const {
      userSignin: { userInfo },
    } = getState();
    try {
      await Axios.delete(`${BASE_URL}/api/users/${userId}/delete`, {
        headers: {
          authorization: `Bearer ${userInfo.token}`,
        },
      });
      toast.success("User successfully deleted!", {
        toastId: customId,
      });
    } catch (error) {
      const err =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;

      toast.error(err, {
        toastId: customId,
      });
      return rejectWithValue(err);
    }
  }
);

export const userDetails = createAsyncThunk(
  "users/userDetails",
  async (userId, { rejectWithValue, getState }) => {
    const {
      userSignin: { userInfo },
    } = getState();
    try {
      const { data } = await Axios.get(`${BASE_URL}/api/users/${userId}`, {
        headers: {
          authorization: `Bearer ${userInfo.token}`,
        },
      });
      return data;
    } catch (error) {
      const err =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return rejectWithValue(err);
    }
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async (user, { rejectWithValue, getState }) => {
    const {
      userSignin: { userInfo },
    } = getState();
    try {
      await Axios.put(`${BASE_URL}/api/users/${user.id}/update`, user, {
        headers: {
          authorization: `Bearer ${userInfo.token}`,
        },
      });
      toast.success("User updated successfully", {
        toastId: customId,
      });
    } catch (error) {
      const err =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      toast.error(err, {
        toastId: customId,
      });
      return rejectWithValue(err);
    }
  }
);

export const createUser = createAsyncThunk(
  "users/createUser",
  async (user, { rejectWithValue, getState }) => {
    const {
      userSignin: { userInfo },
    } = getState();
    try {
      await Axios.post(`${BASE_URL}/auth/`, user, {
        headers: {
          authorization: `Bearer ${userInfo.token}`,
        },
      });
      toast.success("User created successfully", {
        toastId: customId,
      });
    } catch (error) {
      const err =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      toast.error(err, {
        toastId: customId,
      });
      return rejectWithValue(err);
    }
  }
);

const initialState = {
  loading: false,
  error: false,
  userInfo: localStorage?.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null,
  users: [],
  user: {},
  userDeleteSuccess: false,
  userUpdateSuccess: false,
  userCreateSuccess: false,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    signout: (state) => {
      localStorage.removeItem("userInfo");
      sessionStorage.removeItem("activeStore");
      sessionStorage.removeItem("newProduct");
      state.userInfo = {};
    },
    resetUser: (state) => {
      state.loading = false;
      state.userDeleteSuccess = false;
      state.userUpdateSuccess = false;
      state.userCreateSuccess = false;
    },
  },
  extraReducers: {
    [listUsers.pending]: (state) => {
      state.loading = true;
      state.error = false;
    },
    [listUsers.fulfilled]: (state, action) => {
      state.loading = false;
      state.users = action?.payload;
    },
    [listUsers.rejected]: (state, action) => {
      state.loading = false;
      state.error = action?.payload;
    },
    [signin.pending]: (state) => {
      state.loading = true;
      state.error = false;
    },
    [signin.fulfilled]: (state, action) => {
      state.loading = false;
      state.userInfo = action?.payload;
    },
    [signin.rejected]: (state, action) => {
      state.loading = false;
      state.error = action?.payload;
    },
    [deleteUser.pending]: (state) => {
      state.loading = true;
      state.error = false;
    },
    [deleteUser.fulfilled]: (state, action) => {
      state.loading = false;
      state.userDeleteSuccess = true;
      state.users = action?.payload;
    },
    [deleteUser.rejected]: (state, action) => {
      state.loading = false;
      state.error = action?.payload;
    },
    [userDetails.pending]: (state) => {
      state.loading = true;
      state.error = false;
    },
    [userDetails.fulfilled]: (state, action) => {
      state.loading = false;
      state.user = action?.payload;
    },
    [userDetails.rejected]: (state, action) => {
      state.loading = false;
      state.error = action?.payload;
    },
    [updateUser.pending]: (state) => {
      state.loading = true;
      state.error = false;
    },
    [updateUser.fulfilled]: (state, action) => {
      state.loading = false;
      state.userUpdateSuccess = true;
      state.user = action?.payload;
    },
    [updateUser.rejected]: (state, action) => {
      state.loading = false;
      state.error = action?.payload;
    },
    [createUser.pending]: (state) => {
      state.loading = true;
      state.error = false;
    },
    [createUser.fulfilled]: (state, action) => {
      state.loading = false;
      state.userCreateSuccess = true;
      state.user = action?.payload;
    },
    [createUser.rejected]: (state, action) => {
      state.loading = false;
      state.error = action?.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const { reducer, actions } = userSlice;
export const { signout, resetUser } = actions;

export default reducer;
